import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import {
  lineClampVariants,
  textAlignVariants,
  toneVariants,
  truncateVariants,
} from "./shared";

const textVariants = cva("", {
  variants: {
    size: {
      lg: [
        "font-(family-name:--font-family-body)",
        "text-(length:--font-size-body-lg)",
        "leading-(--line-height-24)",
        "font-normal",
        "tracking-[0.5px]",
      ],
      md: [
        "font-(family-name:--font-family-body)",
        "text-(length:--font-size-body-md)",
        "leading-(--line-height-20)",
        "font-normal",
        "tracking-[0.25px]",
      ],
      sm: [
        "font-(family-name:--font-family-body)",
        "text-(length:--font-size-body-sm)",
        "leading-(--line-height-16)",
        "font-normal",
        "tracking-[0.4px]",
      ],
    },
    tone: toneVariants,
    align: textAlignVariants,
    truncate: truncateVariants,
    lineClamp: lineClampVariants,
  },
  defaultVariants: {
    size: "md",
    tone: "default",
  },
});

type TextElement = "p" | "span" | "div";

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "className">,
    VariantProps<typeof textVariants> {
  /** Override the default HTML element (defaults to p) */
  as?: TextElement;
}

/**
 * Text component for body/paragraph content.
 * Uses Inter font family with regular weight.
 *
 * Sizes: large (16px), medium (14px), small (12px).
 */
function Text({
  size = "md",
  tone,
  align,
  truncate,
  lineClamp,
  as = "p",
  ...props
}: TextProps) {
  return React.createElement(as, {
    className: textVariants({ size, tone, align, truncate, lineClamp }),
    ...props,
  });
}

export { Text };
